import './styles.scss';

import { I18n, Translate } from '@utils/i18n';

import { ClipLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import React from 'react';
import { debounce } from 'lodash';
import { useAuth } from '_clean-code/api/hooks/useAuth';
import { useToast, ButtonV3 } from '@pulsarml/avalon-ui';
import MicrosoftLogo from '@components/MicrosoftLogo';
import OktaLogo from '@components/OktaLogo';

const USERNAME_REGEX = /^[\p{L}\p{M}\p{S}\p{N}\p{P}]+$/u;
const OKTA_ALLOWED_DOMAINS = ['carrier.com'];

const LoginV2 = () => {
  const { login, loginWithOkta } = useAuth();
  const { toast } = useToast();
  const [isOktaLoginMode, setIsOktaLoginMode] = React.useState(false);
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const passwordInputRef = React.useRef<HTMLInputElement>(null);
  const oktaEmailInputRef = React.useRef<HTMLInputElement>(null);

  const debouncedLogin = debounce((email: string, password: string) => {
    login.mutate({ email, password });
  }, 300);

  const debouncedOktaLogin = debounce(() => {
    loginWithOkta.mutate();
  }, 300);

  const handleLogin = (event: React.FormEvent) => {
    event.preventDefault();

    if (isOktaLoginMode) {
      const email = oktaEmailInputRef.current?.value.trim().toLowerCase() ?? '';
      const isAllowedOktaDomain = OKTA_ALLOWED_DOMAINS.some((domain) =>
        email.endsWith(`@${domain}`)
      );

      if (!isAllowedOktaDomain) {
        toast({
          variant: 'error',
          title: I18n.t('okta_login_not_configured_for_company')
        });
        return;
      }

      debouncedOktaLogin();
      return;
    }

    if (emailInputRef.current !== null && passwordInputRef.current !== null) {
      const email = emailInputRef.current.value.trim();
      const password = passwordInputRef.current.value;

      if (!USERNAME_REGEX.test(email)) {
        toast({ variant: 'error', title: I18n.t('error_user_does_not_exist') });
        return;
      }

      debouncedLogin(email, password);
    }
  };

  const handleOktaLoginMode = () => {
    setIsOktaLoginMode(true);
  };

  const handleCancelOktaLogin = () => {
    if (oktaEmailInputRef.current !== null) {
      oktaEmailInputRef.current.value = '';
    }

    setIsOktaLoginMode(false);
  };

  return (
    <div className="py-7 px-5">
      <form id="login-form" className="login-form-1" onSubmit={handleLogin}>
        {isOktaLoginMode ? (
          <>
            <div>
              <label htmlFor="lg_okta_username" className="input__label">
                <Translate value="email" />
                &nbsp;*
              </label>
              <div className="input-group py-1">
                <input
                  type="email"
                  className="form-control"
                  id="lg_okta_username"
                  name="okta-email"
                  placeholder={`name@company.com`}
                  required
                  ref={oktaEmailInputRef}
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn mt-4 submit-button submit-button--outline px-4 tw-flex tw-items-center tw-justify-center tw-gap-2"
            >
              {loginWithOkta.isPending ? (
                <ClipLoader
                  size={20}
                  loading={loginWithOkta.isPending}
                  color={'#fff'}
                />
              ) : (
                <>
                  <OktaLogo size={18} />
                  {I18n.t('continue_with_okta')}
                </>
              )}
            </button>

            <ButtonV3
              onClick={handleCancelOktaLogin}
              variant={'link'}
              className="tw-mx-auto tw-mt-4 tw-block tw-no-underline"
            >
              {I18n.t('cancel')}
            </ButtonV3>
          </>
        ) : (
          <>
            <div>
              <label htmlFor="lg_username" className="input__label">
                <Translate value="email" />
                &nbsp;*
              </label>
              <div className="input-group py-1">
                <input
                  type="text"
                  className="form-control"
                  id="lg_username"
                  name="email"
                  placeholder="company@pulsarml.com"
                  required
                  ref={emailInputRef}
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="lg_password" className="input__label">
                <Translate value="password_label" />
                &nbsp;*
              </label>
              <div className="input-group py-1">
                <input
                  type="password"
                  className="form-control"
                  id="lg_password"
                  name="password"
                  placeholder={I18n.t('password_label')}
                  required
                  ref={passwordInputRef}
                />
              </div>
            </div>

            <button type="submit" className="btn mt-4 submit-button px-4">
              {login.isPending ? (
                <ClipLoader size={20} loading color={'#fff'} />
              ) : (
                I18n.t('login_title')
              )}
            </button>

            <div className="text-center form-message mt-4">
              <Translate value="cant_access_your_account" />
              <br />
              <Link to="/forgot-password">
                <Translate value="forgot_password" />
              </Link>
            </div>
            <div className="content mt-3">
              <p className="or">
                <Translate value="or" />
              </p>
            </div>
            <button
              type="button"
              className="btn mt-2 submit-button submit-button--outline px-4 tw-flex tw-items-center tw-justify-center tw-gap-2"
              onClick={handleOktaLoginMode}
            >
              <OktaLogo size={18} />
              {I18n.t('login_with_okta')}
            </button>
            <Link
              to="/login/microsoft"
              className="btn mt-2 submit-button submit-button--outline px-4 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-no-underline"
            >
              <MicrosoftLogo size={18} />
              {I18n.t('login_with_microsoft')}
            </Link>
          </>
        )}
      </form>
    </div>
  );
};

export default LoginV2;
